import React, { useState, useEffect } from 'react';
import { ref, set, update, get } from 'firebase/database';
import { doc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { database, db, app } from '../firebase';

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatTs = (ts) => {
    if (!ts) return '';
    try {
        return new Date(ts).toLocaleString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: true
        }).toUpperCase();
    } catch { return ''; }
};

const PLAN_OPTIONS = [
    { value: 'subs_30d',  label: 'Mensual (30 días)',    days: 30 },
    { value: 'subs_60d',  label: 'Bimestral (60 días)',  days: 60 },
    { value: 'subs_180d', label: 'Semestral (180 días)', days: 180 },
    { value: 'subs_365d', label: 'Anual (365 días)',      days: 365 },
    { value: 'custom',    label: 'Personalizado (días)',  days: null },
];

const TOKENS_PER_MONTH = 2_000_000;

// ─── Fallback: activa todos los nodos directamente desde el frontend ─────────
async function activateAllNodesFallback(uid, email, productId, days, expiryMs, log) {
    const now = Date.now();
    const tokensPerDay = TOKENS_PER_MONTH / 30;
    const newTokens = Math.floor(tokensPerDay * days);

    // 1. licencesPremium
    log('⏳ Actualizando /licencesPremium...');
    const premiumRef = ref(database, `licencesPremium/${uid}`);
    const premiumSnap = await get(premiumRef);
    const cur = premiumSnap.val() || {};
    const expired = !(cur.premiumEndDate && cur.premiumEndDate > now);
    const tokensNotConsumed = expired ? 0 : Math.max(0, (cur.tokensAvailable || 0) - (cur.tokensUsed || 0));
    const finalUsed = expired ? 0 : (cur.tokensUsed || 0);

    await set(premiumRef, {
        userId:               uid || 'manual_admin',
        email:                email.toLowerCase(),
        premiumStartDate:     cur.premiumStartDate || now,
        premiumStartDateNormal: formatTs(cur.premiumStartDate || now),
        premiumEndDate:       expiryMs,
        premiumEndDateNormal: formatTs(expiryMs),
        isActive:             true,
        isPremium:            true,
        tokensAvailable:      tokensNotConsumed + newTokens,
        tokensUsed:           finalUsed,
        tokenPeriodStart:     now,
        tokenPeriodStartNormal: formatTs(now),
        tokenPeriodEnd:       expiryMs,
        tokenPeriodEndNormal: formatTs(expiryMs),
        lastUpdated:          now,
        lastUpdatedNormal:    formatTs(now),
        lastSubscriptionDays: days,
        lastTokensAdded:      newTokens,
        totalTokensEarned:    (cur.totalTokensEarned || 0) + newTokens,
        activatedBy:          'manual_admin_panel',
        purchaseDetails: {
            purchaseToken:   'manual_admin',
            productId:       productId,
            tokensAdded:     newTokens,
            purchaseTime:    now,
            purchaseTimeNormal: formatTs(now)
        }
    });
    log('✅ /licencesPremium actualizado');

    // 2. licenses
    log('⏳ Actualizando /licenses...');
    await set(ref(database, `licenses/${uid}`), {
        userId:           uid || 'manual_admin',
        email:            email.toLowerCase(),
        isPremium:        true,
        isActive:         true,
        licenseType:      productId,
        activationDate:   now,
        premiumEndDate:   expiryMs,
        premiumEndDateNormal: formatTs(expiryMs),
        lastUpdated:      now,
        lastUpdatedNormal: formatTs(now),
        lastPurchaseToken: 'manual_admin',
        paymentProvider:  'manual_admin_panel',
        manualRegistration: true,
    });
    log('✅ /licenses actualizado');

    // 3. users (solo si tenemos UID)
    if (uid) {
        log('⏳ Actualizando /users...');
        await update(ref(database, `users/${uid}`), {
            isPremium:        true,
            premiumEndDate:   expiryMs,
            premiumEndDateNormal: formatTs(expiryMs),
            lastUpdated:      now,
            lastUpdatedNormal: formatTs(now),
        });
        log('✅ /users actualizado');
    } else {
        log('⚠️ Sin UID — /users NO actualizado (búscalo manualmente)');
    }

    // 4. usersweb (web users)
    log('⏳ Actualizando /usersweb...');
    // 4. Firestore license mirror (UID is also the document key).
    if (uid) {
        log('⏳ Actualizando Firestore /licences...');
        try {
            await setDoc(doc(db, 'licenses', uid), {
                userId:         uid,
                email:          email.toLowerCase(),
                activatedAt:    now,
                expiresAt:      expiryMs,
                isActive:       true,
                purchaseToken:  'manual_admin',
                paymentProvider: 'manual_admin_panel',
                lastUpdated:    now,
            }, { merge: true });
            log('✅ Firestore /licences actualizado');
        } catch (fsErr) {
            log(`⚠️ Firestore falló: ${fsErr.message} (el resto está OK)`);
        }
    } else {
        log('⚠️ Sin UID — Firestore /licences NO actualizado');
    }
}

// ─── Component ───────────────────────────────────────────────────────────────
const ManualRegisterPanel = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        uid: '',
        plan: 'subs_30d',
        customDays: 30,
    });
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [result, setResult] = useState(null); // 'success' | 'fallback' | 'error'
    const [emailSuggestions, setEmailSuggestions] = useState([]);

    useEffect(() => { loadEmailSuggestions(); }, []);

    const addLog = (msg) =>
        setLogs(prev => [...prev, { msg, ts: new Date().toLocaleTimeString('es-MX') }]);

    const loadEmailSuggestions = async () => {
        try {
            const emails = new Set();
            const usersSnap = await get(ref(database, 'users'));
            if (usersSnap.exists())
                Object.values(usersSnap.val()).forEach(u => u.email && emails.add(u.email.toLowerCase()));
            const licSnap = await get(ref(database, 'licenses'));
            if (licSnap.exists())
                Object.values(licSnap.val()).forEach(l => l.email && emails.add(l.email.toLowerCase()));
            const webSnap = await get(ref(database, 'usersweb'));
            if (webSnap.exists())
                Object.values(webSnap.val()).forEach(u => u.email && emails.add(u.email.toLowerCase()));
            setEmailSuggestions(Array.from(emails).sort());
        } catch (err) {
            console.warn('Error cargando sugerencias de email:', err);
        }
    };

    const getSelectedPlan = () => PLAN_OPTIONS.find(p => p.value === formData.plan);

    const getDays = () => {
        const plan = getSelectedPlan();
        return (plan?.days ?? parseInt(formData.customDays)) || 30;
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLogs([]);
        setResult(null);

        const email      = formData.email.toLowerCase().trim();
        const uid        = formData.uid.trim();
        const productId  = formData.plan === 'custom' ? 'subs_30d' : formData.plan;
        const days       = getDays();
        const expiryMs   = Date.now() + days * 24 * 60 * 60 * 1000;

        // ── Intento 1: Cloud Function ────────────────────────────────────────
        addLog('🚀 Intentando activar vía Cloud Function...');
        let cloudFunctionOk = false;

        if (uid) {
            try {
                const functions = getFunctions(app, 'us-central1');
                const grantFn = httpsCallable(functions, 'grantTestSubscription');
                const fnResult = await grantFn({ targetEmail: email, targetUid: uid, productId });
                addLog(`✅ Cloud Function exitosa: ${fnResult.data?.message || 'OK'}`);
                cloudFunctionOk = true;
                setResult('success');
            } catch (fnErr) {
                addLog(`⚠️ Cloud Function falló: ${fnErr.message}`);
                addLog('🔄 Iniciando activación manual de respaldo (fallback)...');
            }
        } else {
            addLog('⚠️ Sin UID — se salta Cloud Function y se activa directamente en RTDB.');
        }

        // ── Intento 2: Fallback directo a RTDB ──────────────────────────────
        if (!cloudFunctionOk) {
            try {
                await activateAllNodesFallback(uid, email, productId, days, expiryMs, addLog);
                setResult('fallback');
            } catch (fallbackErr) {
                addLog(`❌ Fallback falló: ${fallbackErr.message}`);
                setResult('error');
                setLoading(false);
                return;
            }
        }

        // ── Registro de auditoría ────────────────────────────────────────────
        try {
            await set(ref(database, `admin_manual_activations/${Date.now()}`), {
                email, uid: uid || 'unknown', productId, days,
                expiryMs, expiryDate: formatTs(expiryMs),
                activatedAt: Date.now(), activatedBy: 'admin_panel',
                method: cloudFunctionOk ? 'cloud_function' : 'rtdb_fallback',
            });
            addLog('📝 Registro de auditoría guardado');
        } catch { /* no crítico */ }

        setLoading(false);
        setFormData({ name: '', email: '', uid: '', plan: 'subs_30d', customDays: 30 });
    };

    const statusColor = { success: '#10b981', fallback: '#f59e0b', error: '#ef4444' };
    const statusIcon  = { success: '✅', fallback: '⚠️', error: '❌' };
    const statusText  = {
        success:  'Activado correctamente vía Cloud Function',
        fallback: 'Activado en modo fallback (RTDB directo)',
        error:    'Error — revisa los logs',
    };

    return (
        <div className="admin-panel-card mrp-card">
            <h3>🛡️ Registro Manual de Premium (Admin)</h3>
            <p className="mrp-subtitle">
                Otorga acceso premium a cualquier usuario. Primero intenta la Cloud Function;<br/>
                si falla, activa todos los nodos de RTDB y Firestore directamente.
            </p>

            <form className="mrp-form" onSubmit={handleRegister}>
                {/* Nombre */}
                <div className="mrp-group">
                    <label>Nombre del Usuario <span className="mrp-opt">(opcional)</span></label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Juan Pérez"
                    />
                </div>

                {/* Email */}
                <div className="mrp-group">
                    <label>Correo Electrónico <span className="mrp-req">*</span></label>
                    <input
                        type="email"
                        required
                        list="mrp-emails-list"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        placeholder="usuario@ejemplo.com"
                    />
                    <datalist id="mrp-emails-list">
                        {emailSuggestions.map(em => <option key={em} value={em} />)}
                    </datalist>
                </div>

                {/* UID */}
                <div className="mrp-group">
                    <label>
                        UID de Firebase Auth <span className="mrp-opt">(recomendado)</span>
                        <span className="mrp-hint"> — permite activar Firestore y /users</span>
                    </label>
                    <input
                        type="text"
                        value={formData.uid}
                        onChange={e => setFormData({ ...formData, uid: e.target.value })}
                        placeholder="abc123XyzUID... (Opcional)"
                    />
                </div>

                {/* Plan */}
                <div className="mrp-row">
                    <div className="mrp-group">
                        <label>Plan <span className="mrp-req">*</span></label>
                        <select
                            value={formData.plan}
                            onChange={e => setFormData({ ...formData, plan: e.target.value })}
                        >
                            {PLAN_OPTIONS.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>

                    {formData.plan === 'custom' && (
                        <div className="mrp-group">
                            <label>Días personalizados <span className="mrp-req">*</span></label>
                            <input
                                type="number"
                                min="1"
                                max="3650"
                                required
                                value={formData.customDays}
                                onChange={e => setFormData({ ...formData, customDays: e.target.value })}
                            />
                        </div>
                    )}
                </div>

                {/* Resumen */}
                <div className="mrp-summary">
                    <span>📅 Duración: <strong>{getDays()} días</strong></span>
                    <span>⏰ Expira: <strong>{formatTs(Date.now() + getDays() * 86400000)}</strong></span>
                    <span>🪙 Tokens: <strong>~{Math.floor(TOKENS_PER_MONTH / 30 * getDays()).toLocaleString()}</strong></span>
                </div>

                <button type="submit" className="btn-save mrp-submit" disabled={loading}>
                    {loading ? '⚙️ Procesando...' : '🛡️ Otorgar Acceso Premium'}
                </button>
            </form>

            {/* Log de actividad */}
            {logs.length > 0 && (
                <div className="mrp-log">
                    <div className="mrp-log-header">
                        {result && (
                            <span className="mrp-status" style={{ color: statusColor[result] }}>
                                {statusIcon[result]} {statusText[result]}
                            </span>
                        )}
                    </div>
                    <div className="mrp-log-body">
                        {logs.map((l, i) => (
                            <div key={i} className="mrp-log-line">
                                <span className="mrp-log-ts">{l.ts}</span>
                                <span>{l.msg}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <style>{`
                .mrp-card { padding: 28px; }
                .mrp-subtitle {
                    color: #888; margin-bottom: 22px;
                    font-size: 0.88em; line-height: 1.5;
                }
                .mrp-form { display: flex; flex-direction: column; gap: 16px; max-width: 560px; }
                .mrp-group { display: flex; flex-direction: column; gap: 5px; }
                .mrp-group label { font-size: 0.83em; font-weight: 700; color: #444; }
                .mrp-opt  { font-weight: 400; color: #999; }
                .mrp-req  { color: #ef4444; }
                .mrp-hint { font-weight: 400; color: #aaa; font-size: 0.9em; }
                .mrp-group input, .mrp-group select {
                    padding: 10px 12px; border: 1px solid #ddd;
                    border-radius: 8px; font-size: 0.95em;
                    background: #fafafa; color: #222; transition: border-color .2s;
                }
                .mrp-group input::placeholder { color: #999; }
                .mrp-group input:focus, .mrp-group select:focus {
                    outline: none; border-color: #6366f1;
                }
                .mrp-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                .mrp-summary {
                    display: flex; flex-wrap: wrap; gap: 12px;
                    padding: 12px 16px; border-radius: 8px;
                    background: #f0f9ff; border: 1px solid #bae6fd;
                    font-size: 0.85em; color: #0369a1;
                }
                .mrp-submit { margin-top: 8px; padding: 13px; font-size: 1em; }
                .mrp-log {
                    margin-top: 20px; border-radius: 10px;
                    border: 1px solid #e5e7eb; overflow: hidden;
                }
                .mrp-log-header {
                    padding: 10px 14px;
                    background: #f9fafb;
                    border-bottom: 1px solid #e5e7eb;
                    font-size: 0.88em; font-weight: 600;
                }
                .mrp-status { font-size: 0.95em; }
                .mrp-log-body {
                    max-height: 260px; overflow-y: auto;
                    padding: 10px 14px; display: flex;
                    flex-direction: column; gap: 4px;
                    background: #1e1e2e;
                }
                .mrp-log-line {
                    display: flex; gap: 10px;
                    font-family: 'Courier New', monospace;
                    font-size: 0.82em; color: #d4d4d8;
                }
                .mrp-log-ts { color: #6b7280; white-space: nowrap; }
            `}</style>
        </div>
    );
};

export default ManualRegisterPanel;
